// Amplify Imports
// =========================================================
import { get, post, put, del } from 'aws-amplify/api'
import { fetchAuthSession } from 'aws-amplify/auth'
// Prime NG Imports
// =========================================================
import { type DialogService } from 'primeng/dynamicdialog'
// Custom Imports
// =========================================================
import { handleError } from 'src/app/utils/error_handling'
import { IsKeyInObj } from 'src/app/utils/global_functions'
import { environment } from 'src/environments/environment'
import {
  type ApiMessagesInterface,
  type ApiDataInterface
} from '../interfaces/data-expected-from-backend/api-data-interface'

// Set the api Name
export const apiName = `${environment.stage}-sizeo-service`

export const displayUserFeedback = (response, componentParent: any, messages: ApiMessagesInterface) => {
  if (typeof messages.error !== 'string' && Object.hasOwn(response, 'message')) {
    messages.error = messages.error(response.message)
  }
  if (response?.is_success) {
    console.log(`${messages.success}`, response)
    if (messages.showSuccessToast) {
      // Get and use from the parent component showSuccess
      componentParent.toastAlert.showSuccess(messages.success)
    }
  } else {
    console.error(`${messages.error}`, response)

    if (messages.showErrorToast) {
      // Get and use from the parent component showError
      componentParent.toastAlert.showError(messages.error)
    } else {
      if (IsKeyInObj(response, 'system_code')) {
        handleError(
          componentParent.dialogService,
          {
            status: response.system_code,
            statusText: messages.error
          },
          response.details
        )
      } else {
        handleError(
          componentParent.dialogService,
          {
            status: 403,
            statusText: 'API Response Error'
          },
          messages.error + '. ' + 'Please contact an administrator.'
        )
      }
    }
  }
}

const getToken = async (): Promise<string> => {
  const session = await fetchAuthSession()
  return session.tokens?.idToken?.toString() ?? ''
}

const parseError = (error: any) => {
  try {
    return error?.response?.body ? JSON.parse(error.response.body) : error
  } catch {
    return error
  }
}

// Execute Get Requests
export const ExecuteGetAPI = async (path: string, componentParent: any, messages: ApiMessagesInterface) => {
  if (!componentParent.clearApiCalls) {
    try {
      const { body } = await get({
        apiName,
        path,
        options: { headers: { Authorization: `Bearer ${await getToken()}` } }
      }).response
      const response = (await body.json()) as unknown as ApiDataInterface
      displayUserFeedback(response, componentParent, messages)
      return response
    } catch (error: any) {
      const fError = parseError(error)
      displayUserFeedback(fError, componentParent, messages)
      return fError
    }
  } else {
    return null
  }
}

// Execute Post Requests
export const ExecutePostAPI = async (path: string, componentParent: any, body: any, messages: ApiMessagesInterface) => {
  if (!componentParent.clearApiCalls) {
    try {
      const { body: responseBody } = await post({
        apiName,
        path,
        options: { headers: { Authorization: `Bearer ${await getToken()}` }, body }
      }).response
      const response = (await responseBody.json()) as unknown as ApiDataInterface
      displayUserFeedback(response, componentParent, messages)
      return response
    } catch (error: any) {
      const fError = parseError(error)
      displayUserFeedback(fError, componentParent, messages)
      return fError
    }
  } else {
    return null
  }
}

// Execute Put Requests
export const ExecutePutAPI = async (path: string, componentParent: any, body: any, messages: ApiMessagesInterface) => {
  if (!componentParent.clearApiCalls) {
    try {
      const { body: responseBody } = await put({
        apiName,
        path,
        options: { headers: { Authorization: `Bearer ${await getToken()}` }, body }
      }).response
      const response = (await responseBody.json()) as unknown as ApiDataInterface
      displayUserFeedback(response, componentParent, messages)
      return response
    } catch (error: any) {
      const fError = parseError(error)
      displayUserFeedback(fError, componentParent, messages)
      return fError
    }
  } else {
    return null
  }
}

// Execute Delete Requests
export const ExecuteDeleteAPI = async (
  path: string,
  componentParent: any,
  messages: ApiMessagesInterface,
  body?: any
) => {
  if (!componentParent.clearApiCalls) {
    try {
      const options: any = { headers: { Authorization: `Bearer ${await getToken()}` } }
      if (body) options.body = body
      const { body: responseBody } = await del({ apiName, path, options }).response
      const response = (await responseBody.json()) as unknown as ApiDataInterface
      displayUserFeedback(response, componentParent, messages)
      return response
    } catch (error: any) {
      const fError = parseError(error)
      displayUserFeedback(fError, componentParent, messages)
      return fError
    }
  } else {
    return null
  }
}
